/**
 * NotificationItem Component
 * Single notification item in the dropdown list
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { InboxOutlined } from '@ant-design/icons';
import type { Notification } from '@/api/types/notification.types';
import {
  useMarkAsRead,
  useArchiveNotification,
} from '@/hooks/useNotifications';
import {
  getNotificationIcon,
  getNotificationColor,
  formatTimestamp,
} from '@/utils/notifications';
import './NotificationItem.css';

interface NotificationItemProps {
  notification: Notification;
  onClick?: () => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const navigate = useNavigate();
  const { mutate: markAsRead } = useMarkAsRead();
  const { mutate: archive } = useArchiveNotification();

  const handleClick = () => {
    // Mark as read
    markAsRead(notification.id);

    // Navigate to action URL if provided
    if (notification.action_url) {
      navigate(notification.action_url);
    }

    // Close dropdown
    onClick?.();
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    archive(notification.id);
  };

  const Icon = getNotificationIcon(notification.category);
  const color = getNotificationColor(notification.priority);
  const isUnread =
    notification.status === 'Notifications.notification_status.UNREAD';

  return (
    <div
      className={`notification-item ${isUnread ? 'notification-item-unread' : ''}`}
      onClick={handleClick}
    >
      <div className="notification-item-icon" style={{ color }}>
        <Icon />
      </div>
      <div className="notification-item-content">
        <div className="notification-item-title">{notification.title}</div>
        <div className="notification-item-description">
          {notification.content}
        </div>
        <div className="notification-item-time">
          {formatTimestamp(notification.created_at)}
        </div>
      </div>
      <div className="notification-item-actions">
        <InboxOutlined onClick={handleArchive} title="Archive" />
      </div>
      {isUnread && <div className="notification-item-dot" />}
    </div>
  );
};
